import { z } from 'zod';
import { ENTITY_TYPES, entityArrayKey, type AnyEntity, type EntityMap, type EntityType, type LedgerSnapshot } from './model';
import { validateEntity, validateLedger } from './validation';

const MONEY_FIELDS: Partial<Record<EntityType, readonly string[]>> = {
  income: ['amount'],
  expense: ['amount'],
  payment: ['amount'],
  transfer: ['amount'],
  adjustment: ['amount'],
  recurrenceRule: ['amount']
};

export type WireEntity = Record<string, unknown>;

export function toWireEntity<T extends EntityType>(type: T, entity: EntityMap[T]): WireEntity {
  const result: WireEntity = { ...entity };
  for (const field of MONEY_FIELDS[type] ?? []) result[field] = (entity as any)[field].toString();
  return result;
}

export function fromWireEntity<T extends EntityType>(type: T, raw: WireEntity): EntityMap[T] {
  const result: WireEntity = { ...raw };
  for (const field of MONEY_FIELDS[type] ?? []) {
    if (typeof result[field] !== 'string' || !/^-?\d+$/.test(result[field] as string)) throw new Error(`${type}: dinheiro inválido.`);
    result[field] = BigInt(result[field] as string);
  }
  validateEntity(type, result as unknown as AnyEntity);
  return result as unknown as EntityMap[T];
}

export interface WireSnapshot {
  schemaVersion: 3;
  entities: Partial<Record<EntityType, WireEntity[]>>;
}

export function snapshotToWire(snapshot: LedgerSnapshot): WireSnapshot {
  const entities: Partial<Record<EntityType, WireEntity[]>> = {};
  for (const type of ENTITY_TYPES) {
    const key = entityArrayKey[type];
    const values = type === 'appSettings' ? [snapshot.settings] : snapshot[key] as AnyEntity[];
    entities[type] = values.map((entity) => toWireEntity(type, entity as any));
  }
  return { schemaVersion: 3, entities };
}

export function snapshotFromWire(wire: WireSnapshot): LedgerSnapshot {
  if (wire.schemaVersion !== 3) throw new Error('Versão de backup não suportada.');
  const get = <T extends EntityType>(type: T) => (wire.entities[type] ?? []).map((raw) => fromWireEntity(type, raw));
  const settings = get('appSettings')[0];
  if (!settings) throw new Error('Backup sem ajustes.');
  const snapshot: LedgerSnapshot = {
    accounts: get('account'), incomeSources: get('incomeSource'), categories: get('category'), incomes: get('income'), expenses: get('expense'), payments: get('payment'), transfers: get('transfer'), adjustments: get('adjustment'), recurrenceRules: get('recurrenceRule'), recurrenceExceptions: get('recurrenceException'), settings
  };
  validateLedger(snapshot);
  return snapshot;
}

export const syncOperationSchema = z.object({
  entityType: z.enum(ENTITY_TYPES),
  entityId: z.string().min(1),
  baseVersion: z.number().int().nonnegative(),
  action: z.enum(['upsert', 'delete']),
  record: z.record(z.string(), z.unknown())
});

export const mutationGroupSchema = z.object({
  mutationId: z.string().uuid(),
  operations: z.array(syncOperationSchema).min(1).max(20)
}).refine((group) => new Set(group.operations.map((operation) => `${operation.entityType}:${operation.entityId}`)).size === group.operations.length, { message: 'Uma entidade só pode aparecer uma vez no grupo.', path: ['operations'] });

export const pushRequestSchema = z.object({
  deviceId: z.string().min(1).max(128),
  mutationGroups: z.array(mutationGroupSchema).min(1).max(10)
});

export type SyncOperation = z.infer<typeof syncOperationSchema>;
export type MutationGroup = z.infer<typeof mutationGroupSchema>;
export type PushRequest = z.infer<typeof pushRequestSchema>;

export interface CanonicalOperation {
  entityType: EntityType;
  entityId: string;
  record: WireEntity;
}

export interface ChangeEnvelope {
  revision: number;
  operations: CanonicalOperation[];
  committedAt: string;
}

export interface PullResponse {
  changes: ChangeEnvelope[];
  nextRevision: number;
  hasMore: boolean;
}

export type PushGroupResult =
  | { mutationId: string; status: 'applied' | 'duplicate'; revision: number; operations: CanonicalOperation[] }
  | { mutationId: string; status: 'conflict'; conflicts: CanonicalOperation[] };

export interface PushResponse { results: PushGroupResult[]; }
